/**
 * The single-line text input for the task builder.
 *
 * Two families, and the split is semantic rather than cosmetic: `literal` is answer content a
 * student is matched against — a keyword, a blank's word, an option label — and is monospaced so
 * whitespace and lookalike characters are visible; `prose` is text ABOUT content — alt text, a
 * transcript, a block name — and is set in the UI face. The codebase already made this distinction
 * by hand (mono in Keyword and FITB, sans in Image and Text); this names it.
 */
/* eslint-disable jsx-a11y/no-autofocus -- `autoFocus` is opt-in per call site and only ever set by
   a field created in response to a deliberate action (DataViz's grid adds a row), where focus
   following the new field is the correct behaviour rather than a steal on page load. Scoped to the
   file because the rule reports on the element and there is no comment position inside `return (`;
   this file has exactly one <input>. */
import type { KeyboardEventHandler, ReactNode, Ref } from 'react';

import { useDelayedStickyError } from '@/components/task/use-settled-value';

export interface TextFieldProps {
  value: string;
  onChange: (value: string) => void;
  /**
   * - `literal` — matched against a student answer (mono), so whitespace and lookalikes show.
   * - `prose` — text ABOUT content: alt text, a transcript, a name.
   *
   * There is deliberately no `expression` variant. A field that accepts `100*pi` is a
   * FormulaField — which renders as a plain input when the task has no variables — and having two
   * components for one field is what produced five `hasVariables ? … : …` ternaries in Numerical
   * and none anywhere else.
   */
  variant: 'literal' | 'prose' | 'unit';
  /** True when this value is faulty NOW. When to SHOW it is decided by the field, not the caller. */
  hasError?: boolean;
  /** Registers the field as a target for `focusIssueField`. */
  fieldId?: string;
  ariaLabel: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  /**
   * For a caller that needs to move focus here itself — the media Label box is focused after a
   * clip is added or removed, so focus lands on the field that replaced the button rather than
   * being dropped to <body>.
   */
  inputRef?: Ref<HTMLInputElement>;
  /**
   * Drop the frame — border, rounding and background — so an enclosing wrapper can own one border
   * around this field and something welded to it. For input GROUPS only, where the two halves must
   * read as a single control: UnitField's symbol picker is the case, matching how FormulaField nests
   * its variables trigger inside its own frame.
   *
   * A prop rather than a `className` override because both are border-width utilities and which one
   * wins is Tailwind emit order, not specificity — the same trap that once flattened a 3px rail to
   * 1px elsewhere in this app.
   */
  seamless?: boolean;
  /**
   * Sits INSIDE running text rather than on a form row — FITB's blanks are portalled into the
   * question's rich text. Fixed width and vertically centred on the line, instead of the
   * form-row default of filling its container.
   */
  inline?: boolean;
  /**
   * An icon sitting inside the field's left edge — a search glass, most often.
   *
   * A prop rather than something a caller positions itself, because the caller cannot reliably
   * make room for it: the input's own `px-3` and a `pl-9` passed through `className` are both
   * padding-left utilities, so which one wins is Tailwind's emit order rather than specificity.
   * `px-3` won, the field kept 12px of padding, and the placeholder printed straight through the
   * icon. Owning both halves here is the only way the spacing cannot be lost — the same reasoning
   * as `seamless` above.
   */
  leadingIcon?: ReactNode;
  /**
   * A CONTROL inside the field's right edge — a clear button, most often.
   *
   * Right padding is reserved here for the same reason `leadingIcon` reserves it on the left: a
   * caller passing `pr-9` through `className` is fighting the base `px-3` over Tailwind's emit
   * order rather than over specificity, and loses. Unlike the leading icon this is NOT made
   * pointer-transparent — a clear button nobody can click is not a clear button.
   */
  trailing?: ReactNode;
  /** A hard cap on length, from the raw inputs that had one. */
  maxLength?: number;
  /** For a field that already has a real <label htmlFor>. */
  id?: string;
  /** Id of the element explaining the current error, wired for screen readers. */
  describedBy?: string;
  /** For a field created in response to an action — DataViz's grid focuses a new row's first cell. */
  autoFocus?: boolean;
  /**
   * Renders a `<textarea>` of `rows` height instead of a single-line input.
   *
   * A shape, not a family — alt text and a transcript are still `text-prose`, and still tint as
   * prose. What changes is only how much of it you can see at once, which is a property of the
   * CONTENT (a sentence describing a photograph, a paragraph of speech) rather than of the kind of
   * text it is. Making it a fourth variant would have said the opposite.
   *
   * Only `prose` is offered multiline. A monospaced answer key that scrolls is not a thing anyone
   * wants, and an expression spanning lines is not an expression.
   */
  rows?: number;
  /**
   * Wires this field as the text half of a combobox.
   *
   * A prop rather than the caller spreading attributes, because they have to land on the INPUT
   * itself — a screen reader reads `aria-expanded` and `aria-activedescendant` off the focused
   * element, and the focused element here is owned by this component. UnitField's suggestion list
   * is the only user.
   */
  combobox?: { expanded: boolean; listId: string; activeId?: string };
}

export function TextField({
  value, onChange, variant, hasError, fieldId, ariaLabel,
  placeholder, disabled, className = '', onFocus, onBlur, onKeyDown, inputRef, seamless = false, inline = false, leadingIcon, trailing, maxLength, id, describedBy, autoFocus, rows, combobox,
}: TextFieldProps) {
  /**
   * The same visibility rule every other field uses: a NEW fault waits for the value to stop
   * changing, an already-visible one stays lit until it is actually gone. Applied here rather than
   * by callers for the same reason FormulaField owns it — several callers pass an ungated flag, and
   * a caller that ALSO gates it defeats the sticky half (its flag goes false mid-typing, which is
   * indistinguishable from a fix, so the marker blinks and the delay restarts).
   */
  const showError = useDelayedStickyError(!!hasError, value);
  /**
   * Whether the caller set its own width, in which case the base must not also set one.
   *
   * There is no class-attribute precedence in CSS: `w-20` and `w-full` are both `width` in the same
   * layer, so which one wins is decided by the order Tailwind EMITS them, not the order they are
   * written. `w-full` is emitted later, so a caller asking for `w-20` silently got a full-width
   * field — which is why the variables panel's name box stretched and squeezed min/max/step, and
   * why Numerical's two unit boxes have been the wrong width since they were migrated.
   *
   * Anchored on a word boundary so `min-w-0` and `max-w-48` — which are companions to `w-full`
   * rather than replacements for it — do not disable it.
   */
  const callerSetsWidth = /(?:^|\s)w-/.test(className);
  const border = showError ? 'border-ws-error-fg' : 'border-ws-border';
  // Mono for `literal` only. It is there so lookalikes show — `l` against `I` against `1` — in
  // content matched verbatim against a student's answer. A unit is matched verbatim too, but it is
  // a short token from a controlled vocabulary rather than free text, so the lookalike risk is low,
  // and mono faces have the WEAKEST coverage of exactly the characters units need: `Ω Å ² ³ ⁻¹ · ‰`
  // fall back to a substitute face at a mismatched weight and size, which is the opposite of legible.
  const face = variant === 'literal' ? 'font-mono' : '';
  const kind = variant === 'unit' ? 'unit' : variant === 'literal' ? 'text-literal' : 'text-prose';
  // Everything below is shared by both shapes deliberately: the tint hook, the error timing, the
  // ARIA and the metrics are properties of the FIELD, and a textarea that quietly diverged on any
  // of them is precisely how image and sound ended up outside the system in the first place.
  const shared = {
    ...(combobox ? {
      role: 'combobox',
      'aria-expanded': combobox.expanded,
      'aria-controls': combobox.listId,
      'aria-activedescendant': combobox.activeId,
      'aria-autocomplete': 'list' as const,
    } : {}),
    id,
    maxLength,
    'data-field-kind': kind,
    'data-field-id': fieldId,
    'aria-label': ariaLabel,
    'aria-invalid': showError || undefined,
    'aria-describedby': describedBy,
    placeholder,
    disabled,
    value,
    onFocus,
    onBlur,
    className: `${inline ? 'inline-block w-24 align-middle px-1.5 py-0.5' : `${callerSetsWidth ? '' : 'w-full'} ${leadingIcon ? 'pl-9' : 'pl-3'} ${trailing ? 'pr-9' : 'pr-3'} py-1.5`} ${seamless ? 'border-0 bg-transparent' : `rounded-md border ${border} bg-ws-btn`} text-sm ${face} text-ws-text outline-none disabled:cursor-not-allowed disabled:opacity-40 placeholder:font-sans placeholder:text-ws-muted ${className}`,
  };

  if (rows != null) {
    return (
      <textarea
        {...shared}
        ref={inputRef as Ref<HTMLTextAreaElement>}
        rows={rows}
        // The handle is the one thing a multiline field loses. These sit in a fixed-width editor
        // panel, so a user-dragged width would only break the column it lives in; height is what
        // `rows` is for.
        className={`${shared.className} resize-none`}
        onChange={e => onChange(e.target.value)}
        onKeyDown={onKeyDown}
      />
    );
  }

  const field = (
    <input
      {...shared}
      ref={inputRef}
      autoFocus={autoFocus}
      type='text'
      onChange={e => onChange(e.target.value)}
      onKeyDown={onKeyDown}
    />
  );

  if (!leadingIcon && !trailing) return field;
  return (
    <div className='relative'>
      {leadingIcon && (
        <span aria-hidden='true' className='pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-ws-muted'>
          {leadingIcon}
        </span>
      )}
      {field}
      {trailing && (
        <span className='absolute right-1.5 top-1/2 flex -translate-y-1/2 items-center'>{trailing}</span>
      )}
    </div>
  );
}
